import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Users } from "lucide-react";
import PortalMockup from "./PortalMockup";
import MobileChatMockup from "./MobileChatMockup";

const usps = [
  "Eén doorlopende LOB-reis",
  "Opdrachten, reflecties en ervaringen bij elkaar",
  "Elke leerling zichtbaar in één LOB-dossier",
  "Meer context voor het gesprek met de mentor",
  "Opleidingen ontdekken en vergelijken",
  "Technologie ondersteunt, de leerling kiest",
];

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative w-full overflow-hidden">
      {/* animated gradient mesh backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-accent via-background to-background" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-primary opacity-[0.10] blur-[160px] rounded-full" />
        <div className="absolute top-32 -left-32 w-[500px] h-[500px] bg-warm opacity-[0.10] blur-[150px] rounded-full" />
        <div className="absolute top-24 -right-32 w-[500px] h-[500px] bg-primary opacity-[0.08] blur-[150px] rounded-full" />
        {/* subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(to right, hsl(var(--border)/0.6) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)/0.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage:
              "radial-gradient(ellipse 70% 55% at 50% 30%, black 40%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 55% at 50% 30%, black 40%, transparent 100%)",
          }}
        />
      </div>

      <div className="container mx-auto max-w-5xl relative px-4 sm:px-6 pt-24 pb-16 lg:pt-32 lg:pb-20 text-center">



        {/* headline */}
        <h1 className="text-[2.6rem] sm:text-[3.4rem] lg:text-[4.4rem] font-extrabold text-secondary leading-[1.05] tracking-tight [text-wrap:balance] max-w-4xl mx-auto">
          LOB dat leerlingen{" "}
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-primary via-[hsl(199,100%,42%)] to-[hsl(212,90%,55%)] bg-clip-text text-transparent">
              stap voor stap
            </span>
          </span>{" "}
          verder brengt.
        </h1>

        <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-relaxed mt-8 mb-10 max-w-2xl mx-auto">
          Een doorlopende digitale LOB-leerlijn waarin leerlingen ontdekken wie ze
          zijn, mogelijkheden onderzoeken en onderbouwde keuzes leren maken — terwijl
          mentoren grip houden op de ontwikkeling van hun klas.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            onClick={() => navigate("/demo")}
            className="px-8 py-6 bg-primary text-primary-foreground hover:bg-primary-hover font-bold rounded-full shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all flex items-center gap-3 text-base"
          >
            Plan een kennismaking
            <ArrowRight className="w-5 h-5" />
          </Button>
          <Button
            variant="outline"
            onClick={() => document.getElementById("zo-werkt-het")?.scrollIntoView({ behavior: "smooth" })}
            className="px-7 py-6 bg-card text-secondary font-bold rounded-full border-2 border-border hover:bg-muted transition-colors text-base flex items-center gap-2.5"
          >
            <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Play className="w-3.5 h-3.5 text-primary fill-primary" />
            </span>
            Bekijk hoe het werkt
          </Button>
        </div>

        {/* social proof line */}
        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4 text-primary" />
          <span className="font-medium">
            Ontwikkeld voor leerlingen, mentoren en decanen
          </span>
        </div>

        {/* platform mockup */}
        <div className="relative mt-16 max-w-4xl mx-auto text-left">
          <div className="absolute -inset-6 bg-gradient-to-tr from-primary/15 via-warm/10 to-primary/10 rounded-[3rem] blur-3xl" />
          <div className="relative">
            <PortalMockup />
          </div>

          {/* floating mobile chatbot mockup */}
          <div className="hidden md:block absolute -right-6 lg:-right-16 -bottom-16 z-20 rotate-[3deg] animate-fade-in">
            <div className="absolute -inset-4 bg-gradient-to-tr from-primary/25 to-warm/15 rounded-[2.5rem] blur-2xl" />
            <div className="relative">
              <MobileChatMockup />
            </div>
          </div>

          {/* floating stat cards */}



          <div className="hidden lg:flex absolute -left-10 -bottom-6 items-center gap-3 bg-card/95 backdrop-blur border border-border rounded-2xl shadow-large px-4 py-3 animate-fade-in">
            <div className="w-10 h-10 rounded-xl bg-warm/15 flex items-center justify-center">
              <ArrowRight className="w-5 h-5 text-warm" />
            </div>
            <div className="text-left">
              <div className="text-lg font-extrabold text-secondary leading-none">
                Van start→keuze
              </div>
              <div className="text-xs text-muted-foreground">één leerlijn</div>
            </div>
          </div>
        </div>

      </div>

      {/* marquee of USPs */}
      <div className="relative py-4 overflow-hidden bg-gradient-to-r from-primary via-[hsl(199,100%,42%)] to-[hsl(212,90%,52%)]">
        <div className="flex gap-10 animate-marquee whitespace-nowrap">
          {[...usps, ...usps, ...usps].map((u, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-2.5 text-sm font-bold text-primary-foreground"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground/80" />
              {u}
            </span>
          ))}
        </div>
      </div>

    </section>
  );
};

export default HeroSection;
